/**
 * Lesson Configuration API
 */
import { apiClient, ApiResponse } from './client'
import type {
  LessonConfiguration,
  LessonConfigurationRequest,
  EffectiveLessonConfigurationResponse,
  ConfigurationTemplateResponse,
  ConfigurationTemplateListResponse,
  ConfigurationTemplateRequest
} from '@/types/configuration'

// ========== Template API ==========

/**
 * 템플릿 목록 조회
 */
export const getTemplates = async (): Promise<ConfigurationTemplateListResponse> => {
  const response = await apiClient.get<ApiResponse<ConfigurationTemplateListResponse>>(
    '/configurations/templates'
  )
  return response.data.data!
}

/**
 * 시스템 템플릿만 조회
 */
export const getSystemTemplates = async (): Promise<ConfigurationTemplateListResponse> => {
  const response = await apiClient.get<ApiResponse<ConfigurationTemplateListResponse>>(
    '/configurations/templates/system'
  )
  return response.data.data!
}

/**
 * 템플릿 상세 조회
 */
export const getTemplate = async (templateId: number): Promise<ConfigurationTemplateResponse> => {
  const response = await apiClient.get<ApiResponse<ConfigurationTemplateResponse>>(
    `/configurations/templates/${templateId}`
  )
  return response.data.data!
}

/**
 * 템플릿 생성
 */
export const createTemplate = async (
  request: ConfigurationTemplateRequest
): Promise<ConfigurationTemplateResponse> => {
  const response = await apiClient.post<ApiResponse<ConfigurationTemplateResponse>>(
    '/configurations/templates',
    request
  )
  return response.data.data!
}

/**
 * 시스템 템플릿 생성 (Super Admin)
 */
export const createSystemTemplate = async (
  request: ConfigurationTemplateRequest
): Promise<ConfigurationTemplateResponse> => {
  const response = await apiClient.post<ApiResponse<ConfigurationTemplateResponse>>(
    '/configurations/templates/system',
    request
  )
  return response.data.data!
}

/**
 * 템플릿 수정
 */
export const updateTemplate = async (
  templateId: number,
  request: ConfigurationTemplateRequest
): Promise<ConfigurationTemplateResponse> => {
  const response = await apiClient.put<ApiResponse<ConfigurationTemplateResponse>>(
    `/configurations/templates/${templateId}`,
    request
  )
  return response.data.data!
}

/**
 * 템플릿 삭제
 */
export const deleteTemplate = async (templateId: number): Promise<void> => {
  await apiClient.delete(`/configurations/templates/${templateId}`)
}

// ========== Curriculum Configuration API (Legacy) ==========

/**
 * 커리큘럼 Configuration 조회
 * @deprecated 간소화된 시스템에서는 Product Configuration 사용 권장
 */
export const getCurriculumConfiguration = async (
  curriculumId: number
): Promise<LessonConfiguration | null> => {
  const response = await apiClient.get<ApiResponse<LessonConfiguration | null>>(
    `/configurations/curriculum/${curriculumId}`
  )
  return response.data.data ?? null
}

// ========== License Configuration API (Legacy) ==========

/**
 * 라이선스 Configuration 조회
 * @deprecated 간소화된 시스템에서는 Class Configuration 사용 권장
 */
export const getLicenseConfiguration = async (
  licenseId: number
): Promise<LessonConfiguration | null> => {
  const response = await apiClient.get<ApiResponse<LessonConfiguration | null>>(
    `/configurations/license/${licenseId}`
  )
  return response.data.data ?? null
}

// ========== Product Configuration API ==========

/**
 * 상품 Configuration 조회
 */
export const getProductConfiguration = async (
  productId: number
): Promise<LessonConfiguration | null> => {
  const response = await apiClient.get<ApiResponse<LessonConfiguration | null>>(
    `/configurations/product/${productId}`
  )
  return response.data.data ?? null
}

/**
 * 상품 Configuration 설정
 */
export const setProductConfiguration = async (
  productId: number,
  configuration: LessonConfiguration
): Promise<void> => {
  await apiClient.put(`/configurations/product/${productId}`, {
    configuration
  } as LessonConfigurationRequest)
}

// ========== Class Configuration API ==========

/**
 * 클래스 Configuration 조회
 */
export const getClassConfiguration = async (
  classId: number
): Promise<LessonConfiguration | null> => {
  const response = await apiClient.get<ApiResponse<LessonConfiguration | null>>(
    `/configurations/class/${classId}`
  )
  return response.data.data ?? null
}

/**
 * 클래스 Configuration 설정
 */
export const setClassConfiguration = async (
  classId: number,
  configuration: LessonConfiguration
): Promise<void> => {
  await apiClient.put(`/configurations/class/${classId}`, {
    configuration
  } as LessonConfigurationRequest)
}

// ========== Effective Configuration API ==========

/**
 * B2C 최종 Configuration 조회
 * 간소화됨: Default → Product
 */
export const getEffectiveConfigurationForB2C = async (
  productId: number
): Promise<EffectiveLessonConfigurationResponse> => {
  const params = new URLSearchParams({ productId: productId.toString() })
  const response = await apiClient.get<ApiResponse<EffectiveLessonConfigurationResponse>>(
    `/configurations/effective/b2c?${params}`
  )
  return response.data.data!
}

/**
 * B2B 최종 Configuration 조회
 * 간소화됨: Default → Product → Class
 */
export const getEffectiveConfigurationForB2B = async (params: {
  productId: number
  classId?: number
}): Promise<EffectiveLessonConfigurationResponse> => {
  const searchParams = new URLSearchParams({
    productId: params.productId.toString()
  })
  if (params.classId) {
    searchParams.append('classId', params.classId.toString())
  }
  const response = await apiClient.get<ApiResponse<EffectiveLessonConfigurationResponse>>(
    `/configurations/effective/b2b?${searchParams}`
  )
  return response.data.data!
}

/**
 * 기본 Configuration 조회
 */
export const getDefaultConfiguration = async (): Promise<LessonConfiguration> => {
  const response = await apiClient.get<ApiResponse<LessonConfiguration>>(
    '/configurations/default'
  )
  return response.data.data!
}

// ========== Media Library API ==========

export interface MediaLibrarySimpleResponse {
  id: number
  title: string
  durationSeconds: number | null
  status: string
}

/**
 * 동영상 자료실 목록 조회 (간단 - 선택용)
 */
export const getMediaLibrarySimple = async (): Promise<MediaLibrarySimpleResponse[]> => {
  const response = await apiClient.get<ApiResponse<MediaLibrarySimpleResponse[]>>(
    '/media-library/simple'
  )
  return response.data.data!
}

